import React, { useCallback } from "react";
import { Stack, StackProps } from "@chakra-ui/react";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useSlide } from "../public/slide";
import { SlidePropsSingle, SelectorValue } from "../public/types";
import { CommonSelect } from "./commonSelect";
import { SingleState, useQuizActions, useQuizSnapshot } from "./state";

import { MyFormLabel } from "./ui";

export type SingleSelectProps = {
  /** Overrides the default click behavior (set value + auto-submit + advance). */
  onOptionClick?: (value: SelectorValue) => void;
  /** Props for the root container of the selector */
  containerProps?: StackProps;
  /** Color of the "(Choose one)" label */
  labelColor?: string;
  /** Hides the "(Choose one)" label */
  hideLabel?: boolean;
} & SlidePropsSingle;

export function SingleSelect(props: SingleSelectProps) {
  const actions = useQuizActions();
  const slideCtx = useSlide();
  const snap = useQuizSnapshot();
  const { t } = useTranslation();
  const { onOptionClick } = props;

  const handleClick = useCallback(
    (value: SelectorValue) => {
      if (onOptionClick) {
        onOptionClick(value);
        return;
      }
      actions.toggleRadioOption(slideCtx.id, value);
    },
    [onOptionClick],
  );

  const selectorState = snap.slideStateByID[slideCtx.id] as
    | SingleState
    | undefined;

  return (
    <Stack spacing={1} {...props.containerProps}>
      {!props.hideLabel && (
        <MyFormLabel mx="auto" color={props.labelColor}>
          {props.label ??
            t("quizLib.single.chooseOne", { defaultValue: "(Choose one)" })}
        </MyFormLabel>
      )}
      <CommonSelect
        handleOptionClick={handleClick}
        isOptionSelected={(o) => selectorState?.value?.value === o.value}
        maxItems={props.maxItems}
      />
    </Stack>
  );
}
